/**
 * Package-local mirrors of the reporting-service structured-query wire shape.
 * Wire compatibility is the contract — do not import from edudirect-admin.
 */

export interface ReportingQueryFilter {
  readonly field: string;
  readonly op: 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'not_in';
  readonly value: string | number | boolean | readonly (string | number)[];
}

export interface ReportingQueryHaving {
  readonly metric: string;
  readonly op: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'between';
  readonly value:
    | string
    | number
    | boolean
    | readonly (string | number | boolean)[];
}

export interface ReportingOrderClause {
  readonly field: string;
  readonly direction: 'asc' | 'desc';
}

export interface ReportingStructuredQueryBody {
  readonly modelId: string;
  readonly metrics: readonly string[];
  readonly dimensions?: readonly string[];
  readonly segments?: readonly string[];
  readonly filters?: readonly ReportingQueryFilter[];
  readonly having?: readonly ReportingQueryHaving[];
  readonly order?: readonly ReportingOrderClause[];
  readonly limit?: number;
  readonly context?: Readonly<Record<string, string | number>>;
}

export interface DataLakeResponse {
  readonly contractVersion: string;
  readonly schema: {
    readonly columns: readonly {
      readonly name: string;
      readonly type: string;
      readonly role: string;
    }[];
  };
  readonly data: readonly Record<string, unknown>[];
  readonly meta: Record<string, unknown>;
}

export interface ReportingErrorResponse {
  readonly error: {
    readonly code: string;
    readonly message: string;
    readonly details?: readonly {
      readonly field?: string;
      readonly reason: string;
    }[];
  };
}

export function isDataLakeResponse(value: unknown): value is DataLakeResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  const version = obj.contractVersion;
  return (
    typeof version === 'string' &&
    version.length > 0 &&
    typeof obj.schema === 'object' &&
    obj.schema !== null &&
    Array.isArray(obj.data) &&
    typeof obj.meta === 'object' &&
    obj.meta !== null
  );
}

export function isReportingError(
  value: unknown,
): value is ReportingErrorResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  if (typeof obj.error !== 'object' || obj.error === null) {
    return false;
  }
  const err = obj.error as Record<string, unknown>;
  return typeof err.code === 'string' && typeof err.message === 'string';
}
